import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  upsertOrganizationPricingConfiguration,
  upsertPlanPricingConfiguration,
  CreatePricingConfigurationPayload,
  getDefaultPricingConfiguration,
  getMinimumPricingConfiguration,
  getPricingConfiguration,
  PricingConfiguration,
  upsertMinimumPricingConfiguration,
} from '@/services/Pricing/pricing-configuration.service';

export const pricingConfigurationApiSlice = createApi({
  reducerPath: 'pricingConfigurationApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['PricingConfiguration', 'DefaultPricingConfiguration', 'MinimumPricingConfiguration'],
  endpoints: (builder) => ({
    getPricingConfiguration: builder.query<
      PricingConfiguration | null,
      { organizationId: string; planId?: string }
    >({
      async queryFn(params) {
        try {
          const data = await getPricingConfiguration(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: ['PricingConfiguration'],
    }),
    upsertOrganizationPricingConfiguration: builder.mutation<
      PricingConfiguration,
      { organizationId: string; payload: CreatePricingConfigurationPayload }
    >({
      async queryFn(params) {
        try {
          const data = await upsertOrganizationPricingConfiguration(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: ['PricingConfiguration'],
    }),
    upsertPlanPricingConfiguration: builder.mutation<
      PricingConfiguration,
      { organizationId: string; planId: string; payload: CreatePricingConfigurationPayload }
    >({
      async queryFn(params) {
        try {
          const data = await upsertPlanPricingConfiguration(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: ['PricingConfiguration'],
    }),
    getDefaultPricingConfiguration: builder.query<PricingConfiguration, { organizationId: string }>(
      {
        async queryFn(params) {
          try {
            const data = await getDefaultPricingConfiguration(params);
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
        providesTags: ['DefaultPricingConfiguration'],
      },
    ),
    getMinimumPricingConfiguration: builder.query<PricingConfiguration, { organizationId: string }>(
      {
        async queryFn(params) {
          try {
            const data = await getMinimumPricingConfiguration(params);
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
        providesTags: ['MinimumPricingConfiguration'],
      },
    ),
    upsertMinimumPricingConfiguration: builder.mutation<
      PricingConfiguration,
      { organizationId: string; payload: CreatePricingConfigurationPayload }
    >({
      async queryFn(params) {
        try {
          const data = await upsertMinimumPricingConfiguration(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: ['MinimumPricingConfiguration'],
    }),
  }),
});

export const {
  useGetPricingConfigurationQuery,
  useLazyGetPricingConfigurationQuery,
  useUpsertOrganizationPricingConfigurationMutation,
  useUpsertPlanPricingConfigurationMutation,
  useGetDefaultPricingConfigurationQuery,
  useGetMinimumPricingConfigurationQuery,
  useUpsertMinimumPricingConfigurationMutation,
} = pricingConfigurationApiSlice;
